import {
  CancelledError,
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { is404Error } from "./error";
import { reportRequestError } from "./error-reporter";

// Query/mutation failures never throw — React Query hands them to components
// as `error` state, so without these observers a failing bookings or
// packages call renders an empty card and nobody hears about it. Cache-level
// onError fires once per settled failure (after the retry policy below gives
// up), is observation only, and cannot alter what the component sees.
const describeKey = (key: unknown): string => {
  try {
    return JSON.stringify(key).slice(0, 200);
  } catch {
    return String(key);
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // cancelQueries() (LineBranchContext on branch switch) reverts and never
      // reaches here today; keep the guard so a library change can't turn
      // every branch switch into a report.
      if (error instanceof CancelledError) return;
      reportRequestError(error, { source: "query", queryKey: describeKey(query.queryKey) });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportRequestError(error, {
        source: "mutation",
        mutationKey: mutation.options.mutationKey
          ? describeKey(mutation.options.mutationKey)
          : undefined,
      });
    },
  }),
  defaultOptions: {
    queries: {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const MAX_FAILURE_COUNT = 3;
        if (is404Error(error)) {
          return false;
        }
        return failureCount < MAX_FAILURE_COUNT;
      },
    },
  },
});
